import type { DestinationPlatformData } from "app/js/src/domain/destinations";
import {
  parseClipPreviewValue,
  type ClipPreviewModel,
} from "app/js/src/domain/clipping-studio/clip-preview-model";
import { AspectRatio, parseAspectRatio } from "app/js/src/domain/clipping-studio/aspect-ratio";

export type DestinationUid = string;
export type IanaTimeZone = string; // e.g. Europe/Moscow

/** Top-level keys that are not destination UIDs (ignored on read). */
const RESERVED_TOP_LEVEL_KEYS = new Set(["version", "shared", "destinations"]);

export interface PublishDestinationMetadataOverrideV1 {
  title?: string;
  tags?: string;
  message?: string;
}

export interface PublishDestinationOverridesV1 {
  aspectRatio: AspectRatio;
  /** Destination platform settings overrides (same shape as Tenant Settings destination platformData). */
  destinationSettings: DestinationPlatformData | null;
}

export interface PublishDestinationStateV1 {
  /** Per-destination preview editor output. */
  clipPreview: ClipPreviewModel | null;
  metadata: PublishDestinationMetadataOverrideV1;
  publish: PublishDestinationOverridesV1;
}

/** In-memory publish overrides model (destinations map). */
export interface PublishModelV1 {
  version: 1;
  destinations: Record<DestinationUid, PublishDestinationStateV1>;
}

export function createEmptyPublishModelV1(_args: {
  timeZone: IanaTimeZone;
}): PublishModelV1 {
  return {
    version: 1,
    destinations: {},
  };
}

export function createDefaultDestinationStateV1(args: {
  timeZone: IanaTimeZone;
  aspectRatio?: unknown;
  destinationSettings?: unknown;
  metadata?: unknown;
  clipPreview?: unknown;
}): PublishDestinationStateV1 {
  const aspectRatio = parseAspectRatio(args.aspectRatio ?? AspectRatio.Aspect16x9);

  const metadataRaw = args.metadata as PublishDestinationMetadataOverrideV1 | null | undefined;
  const metadata: PublishDestinationMetadataOverrideV1 = {
    title: typeof metadataRaw?.title === "string" ? metadataRaw.title : undefined,
    tags: typeof metadataRaw?.tags === "string" ? metadataRaw.tags : undefined,
    message: typeof metadataRaw?.message === "string" ? metadataRaw.message : undefined,
  };

  return {
    clipPreview: parseClipPreviewValue(args.clipPreview),
    metadata,
    publish: {
      aspectRatio,
      destinationSettings: (args.destinationSettings ?? null) as DestinationPlatformData | null,
    },
  };
}

function parseDestinationEntry(
  uid: string,
  v: unknown,
  timeZone: IanaTimeZone,
): PublishDestinationStateV1 | null {
  if (!uid?.trim()) return null;
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const d = v as Record<string, unknown>;
  const publishRaw = (d.publish as Record<string, unknown> | null | undefined) ?? {};
  return createDefaultDestinationStateV1({
    timeZone,
    clipPreview: d.clipPreview,
    metadata: d.metadata,
    aspectRatio: publishRaw.aspectRatio,
    destinationSettings: publishRaw.destinationSettings,
  });
}

export function parsePublishModelV1(args: {
  raw: string | null | undefined;
  timeZone: IanaTimeZone;
}): PublishModelV1 {
  const trimmed = args.raw?.trim();
  if (!trimmed) return createEmptyPublishModelV1({ timeZone: args.timeZone });
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!parsed || typeof parsed !== "object") {
      return createEmptyPublishModelV1({ timeZone: args.timeZone });
    }
    const obj = parsed as Record<string, unknown>;
    if (obj.version !== 1) {
      return createEmptyPublishModelV1({ timeZone: args.timeZone });
    }

    const destinations: Record<DestinationUid, PublishDestinationStateV1> = {};

    // { version, "<destinationUid>": { clipPreview, metadata, publish }, ... }
    for (const [key, v] of Object.entries(obj)) {
      if (RESERVED_TOP_LEVEL_KEYS.has(key)) continue;
      const state = parseDestinationEntry(key, v, args.timeZone);
      if (state) destinations[key] = state;
    }

    return { version: 1, destinations };
  } catch {
    return createEmptyPublishModelV1({ timeZone: args.timeZone });
  }
}

/** Persists as flat JSON: version + destination uid keys at the root. */
export function serializePublishModelV1(model: PublishModelV1): string {
  return JSON.stringify({
    version: model.version,
    ...model.destinations,
  });
}
